import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Leaf, Calculator, DollarSign, TrendUp, ArrowLeft, MagnifyingGlass, Drop, Info } from '@phosphor-icons/react';
import { TurfPulseLogoSVG } from '../components/TurfPulseLogo';
import { DepartmentNav } from '../components/DepartmentNav';
import { PageWhy } from '../components/PageWhy';
import { AmazonDisclosure } from '../components/AmazonDisclosure';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

export const PGRDepartmentPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['products', 'common', 'dashboard']);
  const [products, setProducts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showApplicationDialog, setShowApplicationDialog] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  
  // Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return products;
    const search = productSearch.toLowerCase();
    return products.filter(p => 
      p.name?.toLowerCase().includes(search) ||
      p.active_ingredient?.toLowerCase().includes(search) ||
      p.manufacturer?.toLowerCase().includes(search)
    );
  }, [products, productSearch]);
  
  // Calculator state
  const [area, setArea] = useState('');
  const [areaUnit, setAreaUnit] = useState('sqft'); // sqft or acres
  const [applicationRate, setApplicationRate] = useState('');
  // Water carrier rate (gal per 1000 sq ft). 1.0 is the homeowner default —
  // Primo Maxx label allows 0.5–2 gal/1K, with 1 gal/1K hitting most pump-up
  // sprayer specs for even coverage at typical walking pace.
  const [carrierRate, setCarrierRate] = useState('1.0');
  // Sprayer/vessel capacity in gallons. Most homeowners run a 1- or 2-gal
  // pump sprayer; backpacks are 3–4. This drives the tank-plan math
  // (numTanks, per-tank product oz, per-tank coverage sqft).
  const [vesselSize, setVesselSize] = useState('2');
  // Advanced settings are agronomist-speak (rate per 1K, carrier per 1K).
  // Hidden by default so homeowners just pick product + sprayer + sqft.
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [calculatedAmount, setCalculatedAmount] = useState(null);
  const [calculatedCost, setCalculatedCost] = useState(null);
  // Cached at calc-time so the result card can show "0.30 × 1,733 sq ft = 0.52"
  // and the water-in-tank math without re-deriving from inputs that may have
  // since changed.
  const [calcSnapshot, setCalcSnapshot] = useState(null);

  // When a product is selected, auto-populate the application rate to the
  // midpoint of the product's recommended range — homeowner doesn't have to
  // pick an agronomist-correct rate, and the calculator works on first click.
  // Only pre-fills if the user hasn't typed anything yet (so manual overrides stick).
  useEffect(() => {
    if (!selectedProduct) return;
    if (applicationRate) return; // user already typed
    const min = parseFloat(selectedProduct.application_rate_min);
    const max = parseFloat(selectedProduct.application_rate_max);
    if (Number.isFinite(min) && Number.isFinite(max)) {
      const mid = ((min + max) / 2);
      setApplicationRate(mid.toFixed(2));
    }
  }, [selectedProduct, applicationRate]);

  // Rate-weighted GDD preview — updates live as the user types a rate.
  // Kreuser/Rogan scaling: higher rate → longer suppression window.
  const [ratePreview, setRatePreview] = useState(null);
  useEffect(() => {
    const rate = parseFloat(applicationRate);
    if (!selectedProduct || !rate || rate <= 0) { setRatePreview(null); return; }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API_URL}/api/pgr/rate-preview`, {
          params: { product: selectedProduct.id || selectedProduct.name, rate, grass_type: user?.grass_type || 'bermuda' },
          withCredentials: true,
          signal: ctrl.signal,
        });
        setRatePreview(r.data);
      } catch (e) { /* swallow — non-blocking preview */ }
    }, 250);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [applicationRate, selectedProduct, user]);
  
  // Application form state
  const [appArea, setAppArea] = useState('');
  const [appRate, setAppRate] = useState('');
  const [appNotes, setAppNotes] = useState('');
  const [appDate, setAppDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, applicationsRes] = await Promise.all([
        axios.get(`${API_URL}/api/pgr/products`, { withCredentials: true }),
        axios.get(`${API_URL}/api/applications`, { withCredentials: true })
      ]);
      setProducts(productsRes.data);
      // /api/applications returns {logs: [...]} — not a bare array.
      // Defensive shape-check so a future API change doesn't crash the page.
      const logs = Array.isArray(applicationsRes.data?.logs)
        ? applicationsRes.data.logs
        : Array.isArray(applicationsRes.data)
          ? applicationsRes.data
          : [];
      setApplications(logs.filter(app => app.product_type === 'PGR'));
    } catch (error) {
      if (error.response?.status === 403) {
        // UpgradeGate handles the UI - just silently catch
      } else {
        console.error('Error fetching data:', error);
      }
    }
  };

  const calculateApplication = () => {
    if (!selectedProduct || !area || !applicationRate) {
      toast.error('Please fill in lawn area and pick a product');
      return;
    }

    // Convert area to square feet
    const areaSqFt = areaUnit === 'acres' ? parseFloat(area) * 43560 : parseFloat(area);
    const rate = parseFloat(applicationRate);

    // Calculate amount needed (rate is per 1000 sq ft)
    const amountNeeded = (areaSqFt / 1000) * rate;

    // Carrier water — gal/1K × area, defaults to 1.0 if user didn't override
    const carrier = parseFloat(carrierRate) || 1.0;
    const waterGallons = (areaSqFt / 1000) * carrier;

    // Tank plan — divide the lawn into per-tank sections of equal coverage.
    // Each full tank covers (vesselSize gallons ÷ carrier) × 1000 sq ft. The
    // remainder (if any) is the partial last tank.
    const vessel = parseFloat(vesselSize) || 2.0;
    const sectionSqFt = (vessel / carrier) * 1000;
    const fullTanks = Math.floor(areaSqFt / sectionSqFt);
    const remainderSqFt = areaSqFt - fullTanks * sectionSqFt;
    const tanks = [];
    for (let i = 0; i < fullTanks; i++) {
      tanks.push({
        idx: i + 1,
        product_oz: (sectionSqFt / 1000) * rate,
        water_gal: vessel,
        coverage_sqft: sectionSqFt,
        is_partial: false,
      });
    }
    if (remainderSqFt > 1) {
      // round to 1 sq ft to avoid floating-point junk tanks
      tanks.push({
        idx: tanks.length + 1,
        product_oz: (remainderSqFt / 1000) * rate,
        water_gal: (remainderSqFt / 1000) * carrier,
        coverage_sqft: remainderSqFt,
        is_partial: true,
      });
    }
    if (tanks.length === 0) {
      // Lawn smaller than the vessel can spray — single partial tank.
      tanks.push({
        idx: 1,
        product_oz: amountNeeded,
        water_gal: waterGallons,
        coverage_sqft: areaSqFt,
        is_partial: true,
      });
    }

    setCalculatedAmount(amountNeeded);
    setCalcSnapshot({
      areaSqFt,
      rate,
      carrier,
      waterGallons,
      vessel,
      tanks,
    });

    // Calculate cost if product has pricing
    if (selectedProduct.cost_per_unit) {
      const cost = amountNeeded * selectedProduct.cost_per_unit;
      setCalculatedCost(cost);
    } else {
      setCalculatedCost(null);
    }
  };

  const handleLogApplication = async (e) => {
    e.preventDefault();
    if (!selectedProduct) {
      toast.error('Please select a product');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/applications`,
        {
          product_name: selectedProduct.name,
          product_type: 'PGR',
          application_rate: `${appRate} oz/1000 sq ft on ${appArea} ${areaUnit}`,
          notes: appNotes
        },
        { withCredentials: true }
      );
      toast.success('Application logged successfully!');
      setShowApplicationDialog(false);
      setAppArea('');
      setAppRate('');
      setAppNotes('');
      fetchData();
    } catch (error) {
      toast.error('Failed to log application');
    }
  };

  const getTotalCost = () => {
    return applications.reduce((sum, app) => {
      // Extract rate from application_rate string if possible
      // For now, return 0 as we need to enhance the data model
      return sum;
    }, 0);
  };

  const getProductStats = () => {
    const productCounts = {};
    applications.forEach(app => {
      productCounts[app.product_name] = (productCounts[app.product_name] || 0) + 1;
    });
    return Object.entries(productCounts).map(([name, count]) => ({ name, count }));
  };

  return (
    <div data-testid="pgr-department-page" className="min-h-screen bg-background">
      {/* Department Navigation */}
      <DepartmentNav />

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Page Title */}
        <div>
          <h2 className="text-3xl font-bold flex items-center gap-3">
            <TrendUp weight="duotone" size={32} className="text-primary" />
            PGR Department
          </h2>
          <p className="text-muted-foreground">Plant Growth Regulators for tighter, denser turf and fewer mowings</p>
          <PageWhy testId="pgr-page-why">Keep your turf tight and dark green without mowing every 4 days — time PGR apps by GDD so every application counts.</PageWhy>
        </div>

        {/* Quick Stats */}
        <div className="grid md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Applications</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{applications.length}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Products Used</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{getProductStats().length}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Available Products</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{products.length}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">This Season</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">{applications.length}</div>
              <p className="text-xs text-muted-foreground mt-1">Applications logged</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="calculator" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="calculator">Application Calculator</TabsTrigger>
            <TabsTrigger value="products">Product Library</TabsTrigger>
            <TabsTrigger value="history">Application History</TabsTrigger>
          </TabsList>

          {/* Calculator Tab */}
          <TabsContent value="calculator">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator weight="duotone" size={24} className="text-primary" />
                  PGR Application Calculator
                </CardTitle>
                <CardDescription>
                  Calculate product amount and cost for your application area
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Label Disclaimer */}
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40" data-testid="pgr-label-disclaimer">
                  <span className="text-amber-600 mt-0.5 flex-shrink-0 font-bold text-sm">!</span>
                  <p className="text-xs text-amber-800 dark:text-amber-300">
                    <strong>Always read and follow the product label.</strong> The label is the law. Application rates, timing, and safety precautions vary by product. TurfPulse provides general guidance — defer to the manufacturer's label for exact instructions.
                  </p>
                </div>

                {/* Product Selection - Dropdown */}
                <div>
                  <Label>Select PGR Product</Label>
                  <Select
                    value={selectedProduct?.id || ''}
                    onValueChange={(val) => {
                      const prod = products.find(p => p.id === val);
                      setSelectedProduct(prod || null);
                    }}
                  >
                    <SelectTrigger className="mt-2" data-testid="pgr-product-dropdown">
                      <SelectValue placeholder="Choose a PGR product..." />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.name} — {product.active_ingredient} ({product.application_rate_min}-{product.application_rate_max} oz/1000 sq ft)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Product Cards - Quick Visual Reference */}
                {products.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Or tap to select:</Label>
                    <div className="grid md:grid-cols-3 gap-4 mt-2">
                    {products.map((product) => (
                      <div
                        key={product.id}
                        onClick={() => setSelectedProduct(product)}
                        className={`border rounded-lg p-4 cursor-pointer transition-all ${
                          selectedProduct?.id === product.id
                            ? 'border-primary bg-primary/5 ring-2 ring-primary'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <h4 className="font-bold text-sm">{product.name}</h4>
                        <p className="text-xs text-muted-foreground mt-1">{product.active_ingredient}</p>
                        <p className="text-xs text-muted-foreground">
                          {product.application_rate_min}-{product.application_rate_max} oz/1000 sq ft
                        </p>
                        {product.cost_per_unit && (
                          <div className="mt-2">
                            <p className="text-xs text-primary font-medium">
                              ${product.cost_per_unit.toFixed(2)} / {product.unit_size}
                            </p>
                            <p className="text-[10px] text-muted-foreground/70 italic">Price may vary</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  </div>
                )}

                {selectedProduct && (
                  <>
                    {/* Area Input */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="area">Application Area</Label>
                        <Input
                          id="area"
                          type="number"
                          step="0.01"
                          value={area}
                          onChange={(e) => setArea(e.target.value)}
                          placeholder="Enter area"
                        />
                      </div>
                      <div>
                        <Label htmlFor="area-unit">Unit</Label>
                        <Select value={areaUnit} onValueChange={setAreaUnit}>
                          <SelectTrigger id="area-unit">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="sqft">Square Feet</SelectItem>
                            <SelectItem value="acres">Acres</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Sprayer / vessel select — homeowner-speak. Drives
                        the tank-plan math below ("fill your 2-gal sprayer
                        once" vs "you'll need 3 tank-fills"). */}
                    <div>
                      <Label htmlFor="vessel">Your sprayer</Label>
                      <Select value={vesselSize} onValueChange={setVesselSize}>
                        <SelectTrigger id="vessel" data-testid="pgr-vessel-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1-gallon pump sprayer (handheld)</SelectItem>
                          <SelectItem value="2">2-gallon pump sprayer</SelectItem>
                          <SelectItem value="3">3-gallon backpack sprayer</SelectItem>
                          <SelectItem value="4">4-gallon backpack sprayer</SelectItem>
                          <SelectItem value="5">5-gallon backpack sprayer</SelectItem>
                          <SelectItem value="15">15-gallon tow-behind</SelectItem>
                          <SelectItem value="25">25-gallon tow-behind</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        Tells us how to split your spray into tank-fills.
                      </p>
                    </div>

                    {/* Rate-weighted GDD preview — keep prominent, this is the
                        TurfPulse-unique value-add (connects calc → dashboard).
                        Colorblind-safe: light mode uses dark emerald text on
                        emerald-50 bg (WCAG AA ≥7:1); dark mode keeps the
                        original light-on-dark palette. */}
                    {ratePreview && ratePreview.target_gdd && (
                      <div
                        className="rounded-lg border-2 border-emerald-700 bg-emerald-50 dark:border-emerald-600/40 dark:bg-emerald-950/30 p-3"
                        data-testid="pgr-rate-preview-card"
                      >
                        <div className="flex items-baseline justify-between gap-3">
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-emerald-900 dark:text-emerald-300 font-bold">
                              Re-application target
                            </div>
                            <div className="text-2xl font-extrabold text-emerald-900 dark:text-emerald-200 leading-none mt-1" data-testid="pgr-rate-preview-gdd">
                              {ratePreview.target_gdd} <span className="text-sm font-semibold text-emerald-800 dark:text-emerald-400">GDD · base {ratePreview.base_temp_f}°F</span>
                            </div>
                          </div>
                          {ratePreview.saturated && (
                            <span className="text-[10px] uppercase tracking-wider text-amber-900 dark:text-amber-300 bg-amber-100 dark:bg-amber-400/10 border border-amber-700 dark:border-amber-400/30 rounded-full px-2 py-0.5 font-bold">
                              Saturated
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-emerald-900 dark:text-emerald-200 mt-2 leading-snug flex items-start gap-1.5 font-medium">
                          <Info weight="fill" size={12} className="mt-0.5 flex-shrink-0 text-emerald-700 dark:text-emerald-400" />
                          <span>After this spray, your dashboard <strong className="font-bold">GDD countdown</strong> resets to 0 and counts up to <strong className="font-bold">{ratePreview.target_gdd}</strong> — then it's time to spray again.</span>
                        </p>
                        <p className="text-[10px] text-emerald-800/80 dark:text-slate-500 mt-1.5">
                          Model: {ratePreview.model} · Kreuser/Rogan (UNL) rate-scaling
                        </p>
                      </div>
                    )}

                    {/* Advanced — agronomist controls (rate per 1K + carrier
                        per 1K). Hidden by default; opens for power users who
                        want to tune away from product midpoint. */}
                    <div className="border border-dashed border-border rounded-lg">
                      <button
                        type="button"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        className="w-full px-4 py-2.5 flex items-center justify-between text-xs text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="pgr-advanced-toggle"
                      >
                        <span className="flex items-center gap-2">
                          <Info weight="duotone" size={14} />
                          Advanced — rate &amp; carrier per 1,000 sq ft
                        </span>
                        <span className="text-[10px] uppercase tracking-wider">
                          {showAdvanced ? 'Hide' : 'Show'}
                        </span>
                      </button>
                      {showAdvanced && (
                        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-dashed border-border">
                          <div>
                            <Label htmlFor="rate" className="text-xs">Application rate (oz per 1,000 sq ft)</Label>
                            <Input
                              id="rate"
                              type="number"
                              step="0.01"
                              value={applicationRate}
                              onChange={(e) => setApplicationRate(e.target.value)}
                              placeholder={`${selectedProduct.application_rate_min}–${selectedProduct.application_rate_max}`}
                              data-testid="pgr-rate-input"
                            />
                            <p className="text-[11px] text-muted-foreground mt-1">
                              Product range: {selectedProduct.application_rate_min}–{selectedProduct.application_rate_max} oz/1K. We pre-filled the midpoint.
                            </p>
                          </div>
                          <div>
                            <Label htmlFor="carrier" className="text-xs">Water carrier rate (gal per 1,000 sq ft)</Label>
                            <Input
                              id="carrier"
                              type="number"
                              step="0.1"
                              min="0.25"
                              max="5"
                              value={carrierRate}
                              onChange={(e) => setCarrierRate(e.target.value)}
                              placeholder="1.0"
                              data-testid="pgr-carrier-input"
                            />
                            <p className="text-[11px] text-muted-foreground mt-1">
                              Primo label: 0.5–2 gal/1K. Default 1 gal/1K hits most homeowner pump-sprayer specs.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Calculate Button */}
                    <Button onClick={calculateApplication} className="w-full" size="lg">
                      <Calculator weight="bold" size={20} className="mr-2" />
                      Calculate Amount & Cost
                    </Button>

                    {/* Results */}
                    {calculatedAmount !== null && (
                      <div className="bg-primary/5 border border-primary rounded-lg p-6 space-y-4" data-testid="pgr-calc-result">
                        {/* Tank Plan — the headline output. Tells the user
                            exactly how to load THEIR sprayer: N tanks, per-tank
                            product + water + coverage. Mix-order tip below.
                            Colorblind-safe palette: dark sky-blue text on
                            sky-50 in light mode (~9:1 contrast); inverted in
                            dark mode. */}
                        {calcSnapshot && calcSnapshot.tanks && calcSnapshot.tanks.length > 0 && (
                          <div className="border-t border-border pt-4" data-testid="pgr-tank-plan">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <Drop weight="duotone" size={18} className="text-sky-700 dark:text-sky-400" />
                                <p className="text-sm font-bold text-foreground">
                                  Tank plan · {calcSnapshot.vessel}-gal {calcSnapshot.vessel >= 15 ? 'tow-behind' : calcSnapshot.vessel >= 3 ? 'backpack' : 'pump'} sprayer
                                </p>
                              </div>
                              <span
                                className="text-[10px] uppercase tracking-wider bg-sky-100 dark:bg-sky-500/15 text-sky-900 dark:text-sky-300 border border-sky-700 dark:border-sky-500/30 rounded-full px-2 py-0.5 font-bold"
                                data-testid="pgr-tank-count"
                              >
                                {calcSnapshot.tanks.length} tank{calcSnapshot.tanks.length === 1 ? '' : 's'}
                              </span>
                            </div>
                            <div className="mt-3 space-y-2">
                              {calcSnapshot.tanks.map((t) => (
                                <div
                                  key={t.idx}
                                  className="rounded-md bg-sky-50 dark:bg-sky-950/30 border-2 border-sky-700 dark:border-sky-800/50 px-3 py-2.5"
                                  data-testid={`pgr-tank-${t.idx}`}
                                >
                                  <div className="flex items-baseline justify-between gap-2">
                                    <span className="text-[11px] uppercase tracking-wider text-sky-900 dark:text-sky-300 font-bold">
                                      Tank {t.idx}{t.is_partial && calcSnapshot.tanks.length > 1 ? ' (partial)' : ''}
                                    </span>
                                    <span className="text-[10px] text-sky-800 dark:text-sky-300/80 font-medium">
                                      covers {Math.round(t.coverage_sqft).toLocaleString()} sq ft
                                    </span>
                                  </div>
                                  <p className="text-sm text-sky-950 dark:text-sky-100 mt-1 leading-snug">
                                    <strong className="font-extrabold">{t.product_oz.toFixed(2)} oz</strong> of {selectedProduct.name}
                                    {' + water to '}
                                    <strong className="font-extrabold">{t.water_gal.toFixed(1)} gal</strong>
                                  </p>
                                </div>
                              ))}
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
                              <strong className="text-foreground">Mix order:</strong> fill tank ~half with water, add the product, agitate, then top off to the gallon mark. Spray within 4 hours.
                            </p>
                          </div>
                        )}

                        {/* Totals summary — useful for shopping. */}
                        {calcSnapshot && (
                          <div className="border-t border-border pt-4" data-testid="pgr-totals">
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total product</p>
                                <p className="text-lg font-bold text-primary" data-testid="pgr-product-needed-oz">
                                  {calculatedAmount.toFixed(2)} oz
                                </p>
                              </div>
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Total water</p>
                                <p className="text-lg font-extrabold text-sky-800 dark:text-sky-400" data-testid="pgr-water-gallons">
                                  ~{calcSnapshot.waterGallons.toFixed(1)} gal
                                </p>
                              </div>
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-2 font-mono" data-testid="pgr-math-breakdown">
                              {calcSnapshot.rate.toFixed(2)} oz/1K × {calcSnapshot.areaSqFt.toLocaleString()} sq ft = {calculatedAmount.toFixed(2)} oz
                            </p>
                          </div>
                        )}

                        {calculatedCost !== null && (
                          <div className="border-t border-border pt-4">
                            <p className="text-sm text-muted-foreground">Estimated Cost</p>
                            <p className="text-2xl font-bold text-primary">
                              ${calculatedCost.toFixed(2)}
                            </p>
                          </div>
                        )}

                        <Button 
                          onClick={() => {
                            setAppArea(area);
                            setAppRate(applicationRate);
                            setShowApplicationDialog(true);
                          }}
                          variant="outline"
                          className="w-full"
                        >
                          Log This Application
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <CardTitle>PGR Product Library</CardTitle>
                    <CardDescription>Available plant growth regulators and their specifications</CardDescription>
                  </div>
                  <div className="relative w-full sm:w-64">
                    <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                    <Input
                      placeholder="Search products..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      className="pl-10"
                      data-testid="product-search-input"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredProducts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {productSearch ? `No products found matching "${productSearch}"` : 'No products available'}
                  </div>
                ) : (
                <div className="space-y-4">
                  {filteredProducts.map((product) => (
                    <div key={product.id} className="border border-border rounded-lg p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-bold">{product.name}</h3>
                          <p className="text-sm text-muted-foreground">{product.manufacturer}</p>
                        </div>
                        {product.cost_per_unit && (
                          <div className="text-right">
                            <p className="text-2xl font-bold text-primary">${product.cost_per_unit.toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground">per {product.unit_size}</p>
                            <p className="text-[10px] text-muted-foreground/60 italic">Price may vary — check retailer</p>
                          </div>
                        )}
                      </div>
                      
                      <p className="text-sm text-foreground mb-4">{product.description}</p>
                      
                      <div className="grid md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Active Ingredient</p>
                          <p className="font-medium">{product.active_ingredient}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Application Rate</p>
                          <p className="font-medium">
                            {product.application_rate_min}-{product.application_rate_max} oz/1000 sq ft
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Reapplication Interval</p>
                          <p className="font-medium">{product.reapplication_interval_days} days</p>
                        </div>
                      </div>
                      
                      <div className="mt-4 flex gap-2">
                        <Button 
                          onClick={() => {
                            setSelectedProduct(product);
                            document.querySelector('[value="calculator"]').click();
                          }}
                          variant="outline"
                          size="sm"
                        >
                          Use in Calculator
                        </Button>
                        {product.affiliate_link && (
                          <a href={product.affiliate_link} target="_blank" rel="nofollow sponsored noopener noreferrer">
                            <Button variant="secondary" size="sm" data-testid={`buy-amazon-${product.id}`}>
                              Buy on Amazon <span className="text-xs opacity-70">(paid link)</span>
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Application History</CardTitle>
                <CardDescription>Your PGR application records</CardDescription>
              </CardHeader>
              <CardContent>
                {applications.length > 0 ? (
                  <div className="space-y-4">
                    {applications.map((app) => (
                      <div key={app.id} className="border border-border rounded-lg p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-bold">{app.product_name}</h4>
                            <p className="text-sm text-muted-foreground">{app.application_rate}</p>
                            {app.notes && (
                              <p className="text-sm text-muted-foreground mt-2">{app.notes}</p>
                            )}
                            {/* EPA Weather Data */}
                            {(app.temperature_f || app.wind_speed_mph || app.humidity_percent) && (
                              <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
                                {app.temperature_f && (
                                  <span className="bg-secondary/50 px-2 py-0.5 rounded">{Math.round(app.temperature_f)}°F</span>
                                )}
                                {app.wind_speed_mph && (
                                  <span className="bg-secondary/50 px-2 py-0.5 rounded">
                                    Wind: {Math.round(app.wind_speed_mph)} mph {app.wind_direction || ''}
                                  </span>
                                )}
                                {app.humidity_percent && (
                                  <span className="bg-secondary/50 px-2 py-0.5 rounded">
                                    {Math.round(app.humidity_percent)}% humidity
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground" title="Year-to-date GDD at the moment this app was logged. Snapshot, not a target.">
                              YTD at apply
                            </p>
                            <p className="text-sm font-medium">{Math.round(app.gdd_at_application)} GDD</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(app.applied_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Leaf weight="duotone" size={48} className="mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No applications logged yet</p>
                    <Button onClick={() => setShowApplicationDialog(true)} className="mt-4">
                      Log First Application
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        <AmazonDisclosure />
      </div>

      {/* Application Dialog */}
      <Dialog open={showApplicationDialog} onOpenChange={setShowApplicationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log PGR Application</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLogApplication} className="space-y-4">
            <div>
              <Label>Product</Label>
              <Select 
                value={selectedProduct?.id} 
                onValueChange={(id) => setSelectedProduct(products.find(p => p.id === id))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Area</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={appArea}
                  onChange={(e) => setAppArea(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Unit</Label>
                <Select value={areaUnit} onValueChange={setAreaUnit}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sqft">Sq Ft</SelectItem>
                    <SelectItem value="acres">Acres</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Rate (oz/1000 sq ft)</Label>
              <Input
                type="number"
                step="0.01"
                value={appRate}
                onChange={(e) => setAppRate(e.target.value)}
                required
              />
            </div>
            
            <div>
              <Label>Notes (optional)</Label>
              <Input
                value={appNotes}
                onChange={(e) => setAppNotes(e.target.value)}
                placeholder="Weather conditions, observations, etc."
              />
            </div>
            
            <Button type="submit" className="w-full">
              Save Application
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
